<template>
  <div
    v-editable="blok"
    class="HotSpotImage"
  >
    <div class="HotSpotImageWrap relative">
      <nuxt-img
        v-if="blok.image && blok.image.filename"
        preset="standard"
        :sizes="imageSizes"
        class="h-full w-full object-cover"
        :class="blok.focalPoint"
        :src="blok.image.filename"
        :alt="blok.image.alt"
        loading="lazy"
      />
      <div class="absolute top-0 left-0 w-full h-full">
        <div
          v-for="(item) in blok.hotSpots"
          :key="item._uid"
          :style="{ left: `${item.x?.value}%`, top: `${item.y?.value}%` }"
          class="HotSpotDot"
          :class="[
            visibleItem === item._uid ? 'z-50' : 'z-10',
            'absolute inline-flex w-32 h-32 -translate-x-half -translate-y-half rounded-full bg-bg-inverse/50 border-2 border-lightest/50 group hover:bg-bg-inverse/90 hover:border-lightest/90 hover:cursor-pointer transition-all duration-300 ease-out',
            item.x?.value < 40 ? 'justify-start' : item.x?.value < 60 ? 'justify-center' : 'justify-end',
            item.y?.value < 50 ? 'items-start' : 'items-end'
          ]"
          @mouseenter="handleMouseEnter(item._uid)"
          @mouseleave="handleMouseLeave(item._uid)"
          @click="navigateToProduct(item)"
        >
          <div class="absolute top-half left-half w-12 h-12 group-hover:w-8 group-hover:h-8 bg-bg-base rounded-full -translate-x-half -translate-y-half transition-all duration-300 ease-out" />
          <div
            class="HotSpotContent transition-opacity duration-300 ease-out max-w-190 desk:max-w-224 outline outline-1 outline-bg-inverse/10 shadow-lg shrink-0 p-12 bg-bg-base"
            :class="[
              visibleItem === item._uid ? 'opacity-100 z-50' : 'opacity-0 pointer-events-none z-10',
              item.x?.value < 40 ? '-translate-x-32 desk:translate-x-32' : item.x?.value < 60 ? 'translate-x-0' : 'translate-x-32 desk:-translate-x-32',
              item.y?.value < 40 ? 'translate-y-32' : item.y?.value < 60 ? '-translate-y-32' : '-translate-y-32'
            ]"
            @mouseenter="handleMouseEnter(item._uid)"
            @mouseleave="handleMouseLeave(item._uid)"
            @click.stop
          >
            <ProductCard
              v-if="getHotspotProduct(item)"
              :product="getHotspotProduct(item)"
              :enable-quick-buy="false"
              design="hotSpot"
              :placement="100"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  DatasourceObjectPosition,
  ProductSelector, StoryBlokAsset,
  StoryBlokSlider
} from '#constants/types/storyblok';
import { computed, ref, unref } from 'vue';
import ProductCard from '~/components/product/ProductCard.vue';
import { useGlobalContentStore } from '~/store/globalContent';
import Product, { ProductModel } from '#models/product';
import useLinkReplacer from '~/composables/useLinkReplacer';

const globalContent = useGlobalContentStore();
const { productUrl } = useLinkReplacer();
const router = useRouter();

interface Item {
  x: StoryBlokSlider;
  y: StoryBlokSlider;
  product: ProductSelector[];
}

interface Props {
  hotSpots: Item[];
  image: StoryBlokAsset;
  focalPoint: DatasourceObjectPosition;
  products: ProductSelector[];
}

const props = defineProps<{
  blok: Props,
  products: ProductModel[],
  imgSize?: 'lg' | 'sm',
}>();

const imageSizes = computed(()=> {
  if (props.imgSize && props.imgSize === 'lg') {
    return 'sm:100vw desk:746px';
  }
  return 'sm:50vw desk:369px';
});

const visibleItem = ref(null);
let hideTimeout = null;

const handleMouseEnter = (id) => {
  // Clear any pending hide timeout when entering
  if (hideTimeout) clearTimeout(hideTimeout);
  visibleItem.value = id;
};

const handleMouseLeave = (id) => {
  // Delay hiding by 200ms
  hideTimeout = setTimeout(() => {
    // Only hide if still the same id
    if (visibleItem.value === id) {
      visibleItem.value = null;
    }
  }, 200);
};

const navigateToProduct = (item) => {
  const product = getHotspotProduct(item);
  if (product) {
    router.push(productUrl(product.url));
  }
};

const getHotspotProduct = (hotspot) => {
  if (!hotspot.product || hotspot.product.length === 0) {
    return null;
  }
  const productPartNo = hotspot.product[0].product?.product?.partNo;
  if (!productPartNo) {
    return null;
  }
  const found = products.value.find(prod =>
    prod.partNo === productPartNo || prod.partNumbers?.includes(productPartNo)
  );
  return found || null;
};

const productsPartNo = computed(() => {
  return props.blok.hotSpots
    .flatMap((hotSpot) => hotSpot.product)
    .filter((prodSelector) => prodSelector.product !== '')
    .map((prodSelector) => prodSelector.product.product.partNo)
    .filter((partNo) => partNo !== '');
});

const products = computed(() => props.products || []);
const productsForHotspots = computed(() => {
  const partNos = new Set(productsPartNo.value);
  return products.value.filter((productData) => (
    partNos.has(productData.partNo) ||
    productData.partNumbers?.some((partNo) => partNos.has(partNo))
  ));
});

const jsonLd = {
  '@context': 'https://schema.org/',
  '@type': 'ItemList',
  numberOfItems: productsForHotspots.value.length,
  itemListElement: productsForHotspots.value.map(productData => {
    const productItem = Product.create(productData);
    const activeVariant = productItem?.variants[0];
    return {
      '@type': 'Product',
      url: productItem.url,
      name: productItem.name,
      image: productItem.imageList,
      description: productItem.metaDescription,
      sku: productItem.partNo,
      mpn: productItem.partNo,
      brand: {
        '@type': 'Brand',
        name: productItem.brand.name,
      },
      offers: {
        '@type': 'Offer',
        url: productItem.url,
        priceCurrency: unref(globalContent.getCurrency),
        itemCondition: 'https://schema.org/NewCondition',
        availability: activeVariant?.canAddToCart ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      },
    };
  }),
} as any;
useJsonld(jsonLd);

</script>
